import { format as d3Format } from "d3";
import { PandasDataFrameProto } from "build-ts/dataframe";
import showToast from "hooks/useCustomToast";
import { epochConverterSecond, epochConverterWithTimeZone } from "utils/dateUtils";
import * as XLSX from "xlsx";

// ── Pandas dtype categorisation ────────────────────────────────────────────

/**
 * All pandas dtype families normalised into a single category.
 * Every dtype helper below is derived from this — no category logic lives anywhere else.
 *
 *  NUMERIC   – float16/32/64/128, Float32/64 (nullable), int8–64, Int8–64 (nullable),
 *              uint8–64, UInt8–64 (nullable), complex64/128
 *  DATETIME  – datetime64[ns], datetime64[ns, UTC], datetime64[us/ms/s] (pandas 2.x)
 *  TIMEDELTA – timedelta64[ns/us/ms/s]
 *  BOOLEAN   – bool, boolean (nullable)
 *  CATEGORICAL – category
 *  STRING    – object, string, StringDtype
 *  UNKNOWN   – anything not matched above
 */
export enum PandasDtypeCategory {
  NUMERIC = "NUMERIC",
  DATETIME = "DATETIME",
  TIMEDELTA = "TIMEDELTA",
  BOOLEAN = "BOOLEAN",
  CATEGORICAL = "CATEGORICAL",
  STRING = "STRING",
  UNKNOWN = "UNKNOWN",
}

/**
 * Converts a Python strftime pattern to a js-joda DateTimeFormatter pattern.
 * epochConverterWithTimeZone expects js-joda syntax (e.g. "yyyy-MM-dd"),
 * but format_map sends Python strftime syntax (e.g. "%Y-%m-%d").
 */
export function strftimeToJodaPattern(strftime: string): string {
  return strftime
    .replace(/%Y/g, "yyyy") // 4-digit year
    .replace(/%y/g, "yy") // 2-digit year — must come before any single-letter replacements
    .replace(/%m/g, "MM")
    .replace(/%d/g, "dd")
    .replace(/%H/g, "HH")
    .replace(/%M/g, "mm")
    .replace(/%S/g, "ss")
    .replace(/%b/g, "MMM")
    .replace(/%B/g, "MMMM")
    .replace(/%A/g, "EEEE")
    .replace(/%a/g, "EEE");
}

/** True when the column holds numeric values (right-align, apply numeric format). */
export function isNumericDtype(dtype?: string): boolean {
  return getPandasDtypeCategory(dtype) === PandasDtypeCategory.NUMERIC;
}

/** True when the column holds datetime values (serialised as epoch milliseconds). */
export function isDatetimeDtype(dtype?: string): boolean {
  return getPandasDtypeCategory(dtype) === PandasDtypeCategory.DATETIME;
}

/**
 * True when a cell value is a valid numeric primitive for a given dtype.
 * Combines the dtype check with a runtime safety guard (not NaN, actually a number).
 */
export function isNumericCellValue(value: any, dtype?: string): boolean {
  return isNumericDtype(dtype) && typeof value === "number" && !isNaN(value);
}

/**
 * True when a cell value is a valid epoch-ms primitive for a given dtype.
 * Use this before passing to epochConverterWithTimeZone.
 */
export function isDatetimeCellValue(value: any, dtype?: string): boolean {
  return isDatetimeDtype(dtype) && typeof value === "number" && !isNaN(value);
}

/**
 * Parses a Python dict literal used as a row-level format map entry.
 *
 * The backend produces strings like:
 *   "{'value': '${:,.0f}'}"   (single-quoted Python dict)
 *   '{"value": "${:,.0f}"}' (double-quoted, already valid JSON)
 *
 * Strategy:
 *   - Normalise single quotes → double quotes and attempt JSON.parse — this
 *     handles the vast majority of cases safely and correctly.
 *
 * Returns a plain object mapping column name → format string, e.g.
 *   { "value": "${:,.0f}" }
 */
export function parseRowFormatString(formatString: string): Record<string, string> {
  // Normalise Python single-quote delimiters → double quotes, then parse as JSON.
  // Format strings (e.g. "${:,.0f}", "{:,.1%}") never contain literal single quotes,
  // so this normalisation is always safe. Returns {} on any parse failure so the
  // cell falls back to its column-level formatter.
  try {
    const parsed = JSON.parse(formatString.trim().replace(/'/g, '"'));
    // Guard: JSON.parse can return null, primitives, or arrays — we only want a plain object.
    if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
      return Object.fromEntries(Object.entries(parsed).map(([k, v]) => [k, String(v)]));
    }
  } catch (e) {
    console.warn("[parseRowFormatString] Failed to parse row format string:", formatString, e);
  }
  return {};
}

/**
 * Parses a Python-style numeric format string into its three parts.
 *
 * Python format strings follow the pattern:
 *   <literal prefix>{:<d3-compatible spec>}<literal suffix>
 *
 * Examples:
 *   "$ {:.0f}"  → { prefix: "$ ", spec: ".0f",  suffix: "" }
 *   "{:.2%}"    → { prefix: "",   spec: ".2%",   suffix: "" }
 *
 * Returns null when the string does not contain a {:...} placeholder.
 */
export function parsePythonFormatString(formatString: string): { prefix: string; spec: string; suffix: string } | null {
  const match = formatString.match(/^(.*?)\{:(.+?)\}(.*)$/);
  if (!match) return null;
  const [, prefix, spec, suffix] = match;
  return { prefix, spec, suffix };
}

/**
 * Parses a pandas DataFrame serialised by DataFrame.to_json() (default column-oriented orient).
 *
 * pandas produces JSON shaped like:
 *   { "col_a": { "0": val, "1": val, … }, "col_b": { "0": val, "1": val, … }, … }
 *
 * This function inverts that structure into the row-oriented format the table needs:
 *   columns: ["col_a", "col_b", …]
 *   rows:    [{ col_a: val, col_b: val }, { col_a: val, col_b: val }, …]
 *
 * Row ordering is preserved by iterating the row-index keys of the first column.
 */
export function parsePandasDataJson(dataJson: string): { columns: string[]; rows: Record<string, any>[] } {
  try {
    const parsed: Record<string, Record<string, any>> = JSON.parse(dataJson);
    const columns = Object.keys(parsed);
    if (columns.length === 0) return { columns: [], rows: [] };
    // Get the row indices from the first column
    const rowIndices = Object.keys(parsed[columns[0]]);
    // For each row index, create a row object with the values for each column
    const rows = rowIndices?.map(idx => {
      const row: Record<string, any> = {};
      // For each column, add the value at the current row index
      columns?.forEach(col => {
        row[col] = parsed[col][idx];
      });
      return row;
    });
    return { columns, rows };
  } catch {
    return { columns: [], rows: [] };
  }
}

/**
 * Returns the number of data rows and columns in a PandasDataFrameProto.
 */
export function getPandasDfRowColCount(df?: PandasDataFrameProto): { rows: number; columns: number } {
  if (!df?.data_json) return { rows: 0, columns: 0 };
  try {
    const parsed = JSON.parse(df.data_json);
    const cols = Object.keys(parsed);
    if (cols.length === 0) return { rows: 0, columns: 0 };
    return { rows: Object.keys(parsed[cols[0]!]).length, columns: cols.length };
  } catch {
    return { rows: 0, columns: 0 };
  }
}

/**
 * Maps any pandas dtype string to its PandasDtypeCategory.
 * This is the single source of truth — all is* helpers call this function.
 */
export function getPandasDtypeCategory(dtype?: string): PandasDtypeCategory {
  if (!dtype) return PandasDtypeCategory.UNKNOWN;
  const d = dtype.toLowerCase().trim();

  if (d.startsWith("float") || d.startsWith("int") || d.startsWith("uint") || d.startsWith("complex")) {
    return PandasDtypeCategory.NUMERIC;
  }
  if (d.startsWith("datetime")) return PandasDtypeCategory.DATETIME;
  if (d.startsWith("timedelta")) return PandasDtypeCategory.TIMEDELTA;
  if (d === "bool" || d === "boolean") return PandasDtypeCategory.BOOLEAN;
  if (d.startsWith("category") || d.startsWith("period") || d.startsWith("interval")) {
    return PandasDtypeCategory.CATEGORICAL;
  }
  if (d === "object" || d.startsWith("string") || d.startsWith("sparse")) {
    return PandasDtypeCategory.STRING;
  }
  return PandasDtypeCategory.UNKNOWN;
}

export type PandasCellMeta = { align: "left" | "right"; formatter: (value: any) => string };

export type PandasTableFormatContext = {
  columns: string[];
  rows: Record<string, any>[];
  dtypeMap: Record<string, string>;
  columnMeta: Array<{ col: string; align: "left" | "right"; formatter: (value: any) => string }>;
  rowMeta: Record<number, Record<string, PandasCellMeta>>;
};

/**
 * Builds the shared formatting context used by PandasTable rendering and export.
 * Single source of truth for column/row format maps and per-cell formatters.
 */
export function buildPandasTableFormatContext(df?: PandasDataFrameProto): PandasTableFormatContext {
  const empty: PandasTableFormatContext = {
    columns: [],
    rows: [],
    dtypeMap: {},
    columnMeta: [],
    rowMeta: {},
  };
  if (!df?.data_json) return empty;

  const { columns, rows } = parsePandasDataJson(df.data_json);
  if (columns.length === 0) return { ...empty, columns, rows };

  const columnFormatMap: Record<string, string> = {};
  (df.format_map ?? []).forEach(entry => {
    if (entry.column_name) {
      columnFormatMap[entry.column_name] = entry.format_string ?? "";
    }
  });

  const rowFormatMap: Record<number, Record<string, string>> = {};
  (df.format_map ?? []).forEach(entry => {
    if (entry.row_index !== null && entry.format_string) {
      const idx = Number.parseInt(entry.row_index ?? "");
      if (!Number.isNaN(idx)) {
        rowFormatMap[idx] = parseRowFormatString(entry.format_string);
      }
    }
  });

  const dtypeMap: Record<string, string> = {};
  (df.column_dtypes ?? []).forEach(entry => {
    if (entry.column_name) {
      dtypeMap[entry.column_name] = entry.dtype ?? "";
    }
  });

  const columnMeta = columns.map(col => {
    const { align, formatter } = buildCellMeta(dtypeMap[col], columnFormatMap[col]);
    return { col, align, formatter };
  });

  const rowMeta: Record<number, Record<string, PandasCellMeta>> = {};
  Object.entries(rowFormatMap).forEach(([rowIdxStr, colFormats]) => {
    const rowIdx = Number(rowIdxStr);
    rowMeta[rowIdx] = {};
    Object.entries(colFormats).forEach(([col, formatString]) => {
      if (col in dtypeMap) {
        rowMeta[rowIdx][col] = buildCellMeta(dtypeMap[col], formatString);
      }
    });
  });

  return { columns, rows, dtypeMap, columnMeta, rowMeta };
}

/** Returns the display string for one cell — same resolution order as PandasTable. */
export function getFormattedPandasCellValue(
  ctx: PandasTableFormatContext,
  rowIdx: number,
  colIndex: number,
  rawValue: any
): string {
  const col = ctx.columns[colIndex]!;
  const { formatter } = ctx.columnMeta[colIndex]!;
  const rowCell = ctx.rowMeta[rowIdx]?.[col];
  return rowCell ? rowCell.formatter(rawValue) : formatter(rawValue);
}

/**
 * Extracts display-formatted data from a PandasDataFrameProto into string[][].
 * Row 0 = column headers; subsequent rows = formatted cell strings matching the UI table.
 */
export function extractFormattedDataFromPandasDf(df?: PandasDataFrameProto): string[][] {
  const ctx = buildPandasTableFormatContext(df);
  if (ctx.columns.length === 0) return [];

  return [
    ctx.columns,
    ...ctx.rows.map((row, rowIdx) =>
      ctx.columns.map((col, colIndex) => getFormattedPandasCellValue(ctx, rowIdx, colIndex, row[col]))
    ),
  ];
}

/**
 * Extracts data from a PandasDataFrameProto into a (string | number)[][] suitable for
 * copyRawTableToClipboard / downloadRawTableAsExcel.
 * Row 0 = column headers; subsequent rows = typed cell values.
 *
 * Numeric columns keep their JS number type so that XLSX writes them as number cells,
 * which Excel right-aligns automatically. All other columns are converted to strings.
 */
export function extractRawDataFromPandasDf(df?: PandasDataFrameProto): (string | number)[][] {
  if (!df?.data_json) return [];
  const { columns, rows } = parsePandasDataJson(df.data_json);
  if (columns.length === 0) return [];

  // Build the same dtype lookup used by the table renderer.
  const dtypeMap: Record<string, string> = Object.fromEntries(
    (df.column_dtypes ?? []).map(d => [d.column_name, d.dtype ?? ""])
  );

  return [
    columns,
    ...rows.map(row =>
      columns.map(col => {
        const val = row[col];
        if (val === null || val === undefined) return "";
        // isNumericCellValue combines the dtype check + JS typeof guard.
        const isNumericDtype = getPandasDtypeCategory(dtypeMap[col]) === PandasDtypeCategory.NUMERIC;
        const isValidNumber = typeof val === "number" && !isNaN(val);
        if (isNumericDtype && isValidNumber) return val as number;
        return String(val);
      })
    ),
  ];
}

/**
 * Builds the alignment and formatter for a single cell given its dtype and format string.
 * Shared by both columnMeta (column-level) and rowMeta (row-level) in PandasTable.
 *
 * - Datetime dtype: formatString is a strftime pattern → converts via epochConverterWithTimeZone.
 * - Numeric dtype:  formatString is a Python format spec e.g. "${:,.0f}" → converts via d3Format.
 * - All other dtypes: returns String(value).
 */
export function buildCellMeta(
  dtype: string | undefined,
  formatString: string | undefined
): { align: "left" | "right"; formatter: (value: any) => string } {
  const align: "left" | "right" = getPandasDtypeCategory(dtype) === PandasDtypeCategory.NUMERIC ? "right" : "left";

  if (isDatetimeDtype(dtype)) {
    const jodaPattern = formatString ? strftimeToJodaPattern(formatString) : "yyyy-MM-dd";
    return {
      align,
      formatter: (value: any): string => {
        if (value === null || value === undefined) return "—";
        if (isDatetimeCellValue(value, dtype)) {
          try {
            return epochConverterWithTimeZone(value, jodaPattern, "UTC");
          } catch {
            return String(value);
          }
        }
        return String(value);
      },
    };
  }

  const parsedFmt = formatString ? parsePythonFormatString(formatString) : null;
  const d3Formatter = parsedFmt?.spec
    ? (() => {
        try {
          return d3Format(parsedFmt.spec);
        } catch {
          return null;
        }
      })()
    : null;
  return {
    align,
    formatter: (value: any): string => {
      if (value === null || value === undefined) return "—";
      if (parsedFmt && d3Formatter && isNumericCellValue(value, dtype)) {
        try {
          return `${parsedFmt.prefix}${d3Formatter(Number(value))}${parsedFmt.suffix}`;
        } catch {
          return `${parsedFmt.prefix}${value}${parsedFmt.suffix}`;
        }
      }
      return String(value);
    },
  };
}

// function to copy raw table data to clipboard
export function copyRawTableToClipboard(rawData: (string | number)[][]) {
  if (!rawData || rawData.length === 0) {
    showToast("No data to copy", "", "error");
    return;
  }

  // Use XLSX to produce TSV — same path as the Excel export so number/string types
  // are handled identically and the result pastes cleanly into Excel / Google Sheets.
  const ws = XLSX.utils.aoa_to_sheet(rawData);
  const tableText = XLSX.utils.sheet_to_txt(ws);

  navigator.clipboard
    .writeText(tableText)
    .then(() => {
      showToast("Copied to clipboard", "", "success");
    })
    .catch(err => {
      console.error("Failed to copy table:", err);
      showToast("Failed to copy", "Please try again", "error");
    });
}

// function to download raw table data as Excel file
export function downloadRawTableAsExcel(rawData: (string | number)[][], fileName?: string) {
  if (!rawData || rawData.length === 0) {
    showToast("No data to download", "", "error");
    return;
  }

  // Create worksheet
  const ws = XLSX.utils.aoa_to_sheet(rawData);

  // Create workbook
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Table Data");

  // Generate filename
  const file = fileName || `table_data_${epochConverterSecond(Date.now())}.xlsx`;

  // Write and download
  XLSX.writeFile(wb, file);

  showToast("Downloaded as Excel file", "", "success");
}
